import { useEffect, useState } from 'react';
import { clientFetch } from './api';
import ClientShell from './ClientShell';

const styles = {
  container: {
    padding: '24px',
  },
  tabsContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    borderBottom: '2px solid #1F2937',
  },
  tab: {
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: '#9CA3AF',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.2s',
  },
  tabActive: {
    color: '#3B82F6',
    borderBottomColor: '#3B82F6',
  },
  documentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  documentItem: {
    backgroundColor: '#111827',
    border: '1px solid #1F2937',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    transition: 'all 0.2s',
    cursor: 'pointer',
  },
  documentItemHover: {
    borderColor: '#3B82F6',
    backgroundColor: '#0f172a',
  },
  documentIcon: {
    fontSize: '32px',
    minWidth: '40px',
    textAlign: 'center',
  },
  documentContent: {
    flex: 1,
  },
  documentTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#FFF',
    marginBottom: '4px',
  },
  documentMeta: {
    display: 'flex',
    gap: '16px',
    fontSize: '13px',
    color: '#9CA3AF',
  },
  categoryBadge: {
    backgroundColor: '#1F2937',
    color: '#3B82F6',
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  downloadLink: {
    color: '#3B82F6',
    textDecoration: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    padding: '8px 12px',
    borderRadius: '6px',
    transition: 'background-color 0.2s',
  },
  downloadLinkHover: {
    backgroundColor: '#1F2937',
  },
  loadingText: {
    color: '#9CA3AF',
    fontSize: '14px',
  },
  errorText: {
    color: '#EF4444',
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9CA3AF',
  },
};

const categoryIcons = {
  contract: '📋',
  report: '📊',
  onboarding: '🚀',
  general: '📄',
  all: '📑',
};

export default function ClientDocuments() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = ['all', 'contract', 'report', 'onboarding', 'general'];

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const data = await clientFetch('/client/portal/documents');
        setDocuments(Array.isArray(data) ? data : data.documents || []);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load documents');
        console.error('Documents error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const filteredDocuments = activeCategory === 'all'
    ? documents
    : documents.filter((doc) => doc.category === activeCategory);

  const getDocumentIcon = (category) => {
    return categoryIcons[category] || '📄';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <ClientShell title="Documents">
        <div style={styles.container}>
          <p style={styles.loadingText}>Loading documents...</p>
        </div>
      </ClientShell>
    );
  }

  if (error) {
    return (
      <ClientShell title="Documents">
        <div style={styles.container}>
          <p style={styles.errorText}>Error: {error}</p>
        </div>
      </ClientShell>
    );
  }

  return (
    <ClientShell title="Documents">
      <div style={styles.container}>
        {/* Category Tabs */}
        <div style={styles.tabsContainer}>
          {categories.map((category) => (
            <button
              key={category}
              style={{
                ...styles.tab,
                ...(activeCategory === category ? styles.tabActive : {}),
              }}
              onClick={() => setActiveCategory(category)}
              onMouseEnter={(e) => {
                if (activeCategory !== category) {
                  e.target.style.color = '#FFF';
                }
              }}
              onMouseLeave={(e) => {
                if (activeCategory !== category) {
                  e.target.style.color = '#9CA3AF';
                }
              }}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        {/* Documents List */}
        {filteredDocuments && filteredDocuments.length > 0 ? (
          <div style={styles.documentsList}>
            {filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                style={styles.documentItem}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3B82F6';
                  e.currentTarget.style.backgroundColor = '#0f172a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#1F2937';
                  e.currentTarget.style.backgroundColor = '#111827';
                }}
              >
                <div style={styles.documentIcon}>{getDocumentIcon(doc.category)}</div>
                <div style={styles.documentContent}>
                  <div style={styles.documentTitle}>{doc.title}</div>
                  <div style={styles.documentMeta}>
                    <span>
                      <span style={styles.categoryBadge}>{doc.category}</span>
                    </span>
                    <span>{formatFileSize(doc.fileSize)}</span>
                    <span>{doc.date}</span>
                  </div>
                </div>
                <a
                  href={`#download-${doc.id}`}
                  style={styles.downloadLink}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#1F2937';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  ↓ Download
                </a>
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.emptyState}>
            <p>No documents found in this category</p>
          </div>
        )}
      </div>
    </ClientShell>
  );
}
